/**
 * SCWT-PM-V2 Phase 6 · Send-a-corrective-lead drawer.
 *
 * Manual creation only (per Phase 6 approval). PM fills a lightweight
 * form:
 *   • Property (from managed portfolio)
 *   • Report ID (paste from published-report page — link opens it)
 *   • Corrective actions (freeform code + category rows)
 *   • Optional message
 *
 * We POST to `/api/property-manager/corrective-leads`. The backend
 * derives landlord_id + service_partner_id from the property when
 * they're not provided; if either is missing, we surface the specific
 * error the API returns so the PM knows what to correct.
 */
import { useEffect, useState } from "react";
import { X, Plus, Trash2, Send } from "lucide-react";
import api from "@/lib/api";

const EMPTY_ACTION = { code: "", category: "", severity: "medium", requires_site_visit: false };

// Canonical categories from the PWS Lead Value framework — kept short
// so the PM can pick without training. Free-text `code` still allowed.
const CATEGORIES = [
  "filtration", "uv_disinfection", "tank_cleaning", "calcite_correction",
  "microbial_remediation", "chemical_treatment", "reticulation", "other",
];
const SEVERITIES = ["low", "medium", "high"];

export default function PmCreateCorrectiveLeadDrawer({ open, onClose, onCreated }) {
  const [properties, setProperties] = useState([]);
  const [propId, setPropId]         = useState("");
  const [reportId, setReportId]     = useState("");
  const [message, setMessage]       = useState("");
  const [actions, setActions]       = useState([{ ...EMPTY_ACTION }]);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);

  // Reset the form each time it opens + fetch the PM's portfolio for
  // the property picker. Portfolio is small — a single request is fine.
  useEffect(() => {
    if (!open) return;
    setPropId(""); setReportId(""); setMessage("");
    setActions([{ ...EMPTY_ACTION }]); setError(null);
    api.get("/property-manager/properties")
      .then(({ data }) => setProperties(
        (data.items || []).map((it) => ({
          id: it.property?.id, address: it.property?.address,
          landlord_id: it.property?.landlord_id,
          service_partner_id: it.property?.service_partner_id
            || it.property?.managed_by_partner_id, // fallback
        })).filter((p) => p.id),
      ))
      .catch(() => setProperties([]));
  }, [open]);

  if (!open) return null;

  const selectedProperty = properties.find((p) => p.id === propId);

  const updateAction = (idx, patch) => {
    setActions((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addAction = () => setActions((r) => [...r, { ...EMPTY_ACTION }]);
  const removeAction = (idx) => setActions((r) => r.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e?.preventDefault();
    setError(null);
    if (!propId)   { setError("Pick a property."); return; }
    if (!reportId) { setError("Paste the published report ID."); return; }
    if (!selectedProperty?.landlord_id) {
      setError("This property has no active landlord link yet — link a landlord first."); return;
    }
    if (!selectedProperty?.service_partner_id) {
      setError("No PWS Partner is assigned to this property. Add one under Local Partner first."); return;
    }
    const cleanActions = actions
      .map((a) => ({
        code:  (a.code || "").trim(),
        category: (a.category || "").trim(),
        severity: a.severity || "medium",
        requires_site_visit: !!a.requires_site_visit,
      }))
      .filter((a) => a.code || a.category);
    if (cleanActions.length === 0) {
      setError("Add at least one recommended action."); return;
    }
    const codes = cleanActions.map((a) => a.code).filter(Boolean);

    setSaving(true);
    try {
      await api.post("/property-manager/corrective-leads", {
        report_id: reportId.trim(),
        property_id: propId,
        landlord_id: selectedProperty.landlord_id,
        service_partner_id: selectedProperty.service_partner_id,
        recommended_actions: cleanActions,
        corrective_action_codes: codes,
        optional_pm_message: message.trim(),
      });
      onCreated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail
        : (detail?.message || "Could not create the lead."));
    } finally { setSaving(false); }
  };

  return (
    <div
      data-testid="pm-corrective-lead-drawer"
      className="fixed inset-0 z-50 flex justify-end bg-slate-900/50"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-700">SCWT-PM-V2 · Phase 6</div>
            <h3 className="font-display text-lg font-bold text-slate-900 mt-0.5">Send corrective lead</h3>
          </div>
          <button
            type="button"
            data-testid="pm-corrective-lead-drawer-close"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <form onSubmit={submit} className="p-5 space-y-5">
          {/* Property picker */}
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600">
              Property
            </label>
            <select
              data-testid="pm-corrective-lead-property"
              value={propId}
              onChange={(e) => setPropId(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-xl border border-slate-300 px-3 text-sm"
            >
              <option value="">Choose a managed property…</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.address}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600">
              Published report ID
            </label>
            <input
              data-testid="pm-corrective-lead-report-id"
              value={reportId}
              onChange={(e) => setReportId(e.target.value)}
              placeholder="e.g. RPT-2026-000123"
              maxLength={80}
              className="mt-1.5 w-full h-11 rounded-xl border border-slate-300 px-3 text-sm"
            />
            <p className="mt-1 text-[11px] text-slate-500">
              Copy this from the published report page. The Partner will see
              a summary; the full report only if landlord consent allows.
            </p>
          </div>

          {/* Recommended actions */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600">
                Recommended actions
              </label>
              <button
                type="button"
                onClick={addAction}
                data-testid="pm-corrective-lead-add-action"
                className="text-xs text-cyan-700 hover:text-cyan-900 font-semibold inline-flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add row
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {actions.map((a, idx) => (
                <div
                  key={idx}
                  data-testid={`pm-corrective-lead-action-${idx}`}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2"
                >
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      data-testid={`pm-corrective-lead-action-code-${idx}`}
                      value={a.code}
                      onChange={(e) => updateAction(idx, { code: e.target.value })}
                      placeholder="Code (e.g. UV-INSTALL)"
                      maxLength={60}
                      className="h-9 rounded-lg border border-slate-300 px-2.5 text-sm"
                    />
                    <select
                      data-testid={`pm-corrective-lead-action-category-${idx}`}
                      value={a.category}
                      onChange={(e) => updateAction(idx, { category: e.target.value })}
                      className="h-9 rounded-lg border border-slate-300 px-2.5 text-sm"
                    >
                      <option value="">Category…</option>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      data-testid={`pm-corrective-lead-action-severity-${idx}`}
                      value={a.severity}
                      onChange={(e) => updateAction(idx, { severity: e.target.value })}
                      className="h-8 rounded-lg border border-slate-300 px-2 text-xs"
                    >
                      {SEVERITIES.map((s) => <option key={s} value={s}>{s} severity</option>)}
                    </select>
                    <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                      <input
                        data-testid={`pm-corrective-lead-action-site-visit-${idx}`}
                        type="checkbox"
                        checked={!!a.requires_site_visit}
                        onChange={(e) => updateAction(idx, { requires_site_visit: e.target.checked })}
                      />
                      requires site visit
                    </label>
                    {actions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAction(idx)}
                        className="ml-auto text-rose-600 hover:text-rose-900 inline-flex items-center gap-1 text-xs"
                      >
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Optional message */}
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600">
              Optional message to Partner
            </label>
            <textarea
              data-testid="pm-corrective-lead-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="e.g. Landlord prefers weekday appointments."
              className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm resize-none"
            />
            <div className="mt-1 text-[10px] text-slate-400 text-right">{message.length}/500</div>
          </div>

          {error && (
            <div
              data-testid="pm-corrective-lead-drawer-error"
              className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800"
            >
              {error}
            </div>
          )}

          <div className="rounded-xl bg-cyan-50 border border-cyan-200 p-3 text-[11px] text-cyan-900 leading-relaxed">
            <strong className="font-semibold">Consent guardrails:</strong> the
            Partner will see a summary and the suburb only. The landlord&apos;s
            name, email and phone are shared with the Partner <em>after</em> they
            accept — and only if landlord consent to share contact is currently
            active. Offer expires in 72 hours.
          </div>

          <button
            type="submit"
            data-testid="pm-corrective-lead-submit"
            disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white font-semibold py-2.5"
          >
            <Send className="w-4 h-4" /> {saving ? "Sending…" : "Send lead"}
          </button>
        </form>
      </div>
    </div>
  );
}
